import React, { useState } from "react";

import Button from "@atlaskit/button-next/new";
import DropdownMenu, {
    DropdownItemGroup,
    CustomTriggerProps
} from "@atlaskit/dropdown-menu";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import { Box, xcss } from "@atlaskit/primitives-next";

import { FastshiftTableFilterDropdownItem } from "./fastshift-table-filter-dropdown-item";
import { Option } from "./types";

interface FastshiftTableFilterDropdownProps<T extends string> {
    label: React.ReactNode;
    options: Option<T>[];
    customOptionContent?: (option: Option<T>) => React.ReactNode;
    onOptionClick?: (option: Option<T>) => void;
}

interface DropdownTriggerProps<T extends HTMLElement>
    extends CustomTriggerProps<T> {
    label: React.ReactNode;
}

const labelStyle = xcss({
    marginInlineEnd: "space.100",
    display: "inline-block"
});

const DropdownTrigger = ({
    label,
    onClick,
    isSelected,
    triggerRef,
    ...rest
}: DropdownTriggerProps<HTMLButtonElement>) => {
    return (
        <Button
            {...rest}
            ref={triggerRef}
            onClick={onClick}
            isSelected={isSelected}
        >
            <Box xcss={labelStyle}>{label}</Box>
            <ChevronDownIcon label="Chevron down icon" size="small" />
        </Button>
    );
};

export const FastshiftTableFilterDropdown = <T extends string>({
    label,
    options,
    customOptionContent,
    onOptionClick
}: FastshiftTableFilterDropdownProps<T>) => {
    const [selectedOption, setSelectedOption] = useState<Option<T> | null>(
        null
    );

    return (
        <DropdownMenu
            trigger={(props: CustomTriggerProps<HTMLButtonElement>) =>
                DropdownTrigger({
                    ...props,
                    label: selectedOption
                        ? customOptionContent
                            ? customOptionContent(selectedOption)
                            : selectedOption.label
                        : label,
                    isSelected: !!selectedOption
                })
            }
            spacing="compact"
            shouldRenderToParent
        >
            <DropdownItemGroup>
                {options.map((option) => (
                    <FastshiftTableFilterDropdownItem
                        key={option.value}
                        option={option}
                        onClick={() => {
                            setSelectedOption(option);
                            onOptionClick?.(option);
                        }}
                        isSelected={selectedOption?.value === option.value}
                        customContent={
                            customOptionContent
                                ? customOptionContent(option)
                                : undefined
                        }
                    />
                ))}
            </DropdownItemGroup>
        </DropdownMenu>
    );
};
